import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
    TextField
} from '@mui/material';
import { Plus, Trash2, SquarePen, Upload, X, Link as LinkIcon } from 'lucide-react';
import { getAllSocialMedia, createSocialMedia, updateSocialMedia, deleteSocialMedia } from '../../Services/ContentHub/SocialMediaApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const SocialMedia = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [socialMedia, setSocialMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fieldErrors, setFieldErrors] = useState({});

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentSocial, setCurrentSocial] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        link: ''
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedSocialId, setSelectedSocialId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const fetchSocialMedia = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllSocialMedia();
            if (response && response.success) {
                setSocialMedia(response.data);
            } else {
                showSnackbar(response?.message || "Failed to load social media links.", "error");
            }
        } catch (err) {
            showSnackbar("Failed to fetch social media links. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchSocialMedia();
    }, [fetchSocialMedia]);

    const handleOpenModal = (social = null) => {
        setFieldErrors({});
        if (social) {
            setIsEdit(true);
            setCurrentSocial(social);
            setFormData({
                name: social.name || '',
                link: social.link || ''
            });
            setImagePreview(social.image);
        } else {
            setIsEdit(false);
            setCurrentSocial(null);
            setFormData({
                name: '',
                link: ''
            });
            setImagePreview(null);
        }
        setSelectedImage(null);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentSocial(null);
        setFormData({
            name: '',
            link: ''
        });
        setSelectedImage(null);
        setImagePreview(null);
        setFieldErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (name === 'name') {
            newValue = value.replace(/[^a-zA-Z\s]/g, '');
        }
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (!file.type.startsWith("image/")) {
                setFieldErrors(prev => ({ ...prev, image: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed" }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = "";
                return;
            }

            setSelectedImage(file);

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);

            if (fieldErrors.image) {
                setFieldErrors(prev => ({ ...prev, image: null }));
            }
            if (fieldErrors.general) {
                setFieldErrors(prev => ({ ...prev, general: null }));
            }
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fieldErrors.image) {
            setFieldErrors(prev => ({ ...prev, image: null }));
        }
        if (fieldErrors.general) {
            setFieldErrors(prev => ({ ...prev, general: null }));
        }
    };

    const handleSubmit = async () => {
        const errors = {};
        if (!formData.name.trim()) {
            errors.name = "Platform Name is required";
        }
        if (!formData.link.trim()) {
            errors.link = "Link is required";
        }

        if (selectedImage && !selectedImage.type.startsWith("image/")) {
            errors.image = "Only image files are allowed";
            showSnackbar("Only image files are allowed", "error");
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        setFieldErrors({});

        setSubmitting(true);

        const formPayload = new FormData();
        formPayload.append('name', formData.name);
        formPayload.append('link', formData.link);

        if (selectedImage) {
            formPayload.append('socialMediaImage', selectedImage);
        }

        try {
            if (isEdit) {
                await updateSocialMedia(currentSocial.id, formPayload);
                showSnackbar("Social link updated successfully!", "success");
            } else {
                await createSocialMedia(formPayload);
                showSnackbar("Social link created successfully!", "success");
            }

            handleCloseModal();
            fetchSocialMedia();
        } catch (err) {
            console.error(err);
            const serverError = err.response?.data;
            const errorMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                `Failed to ${isEdit ? 'update' : 'create'} social media`;

            showSnackbar(errorMsg, "error");

            const errorsObj = {};
            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                serverError.details.forEach(item => {
                    errorsObj[item.field] = item.message;
                    if (item.field === 'general' && item.message.toLowerCase().includes('image')) {
                        errorsObj.image = item.message;
                    }
                });
                setFieldErrors(errorsObj);
            } else if (serverError && serverError.errors && Array.isArray(serverError.errors) && serverError.errors.length > 0) {
                if (serverError.errors[0].toLowerCase().includes("link")) {
                    errorsObj.link = serverError.errors[0];
                } else if (serverError.errors[0].toLowerCase().includes("image")) {
                    errorsObj.image = serverError.errors[0];
                } else {
                    errorsObj.name = serverError.errors[0];
                }
                setFieldErrors(errorsObj);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedSocialId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedSocialId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteSocialMedia(selectedSocialId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedSocialId(null);
                    showSnackbar("Social link deleted successfully!", "success");
                    fetchSocialMedia();
                } else {
                    setDeleteError(response?.message || "Failed to delete social media link");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete social media link";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Social Media
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            disableElevation
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={() => handleOpenModal()}
                            sx={{
                                bgcolor: '#FFBD65',

                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                textTransform: 'none',
                                height: '40px',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Social Link
                        </Button>
                    </PermissionWrapper>
                </Box>

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1000 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '80px' }}>Icon</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '180px' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Link</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : socialMedia.length > 0 ? (
                                    socialMedia.map((social, idx) => (
                                        <TableRow key={social.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Avatar
                                                    src={social.image}
                                                    variant="rounded"
                                                    sx={{ width: 32, height: 32, borderRadius: '4px', bgcolor: '#F5F5F5' }}
                                                >
                                                    {!social.image && <LinkIcon size={16} color="#FFBD65" />}
                                                </Avatar>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 500, color: '#222', fontSize: '0.8rem' }}>{social.name}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem' }}>
                                                <Typography
                                                    component="a"
                                                    href={social.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        color: '#FFBD65',
                                                        textDecoration: 'none',
                                                        '&:hover': { textDecoration: 'underline' },
                                                        fontSize: '0.8rem'
                                                    }}
                                                >
                                                    {social.link}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {social.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {social.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(social.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(social.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FFBD65' }}
                                                                onClick={() => handleOpenModal(social)}
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FF4D49' }}
                                                                onClick={() => handleDeleteClick(social.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No social media links found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', borderBottom: '1px solid #F0F0F0', py: 2, fontSize: '1.1rem' }}>
                        {isEdit ? 'Edit Social Link' : 'Add Social Link'}
                    </DialogTitle>
                    <DialogContent sx={{ py: 3 }}>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Platform Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g. Facebook, Instagram"
                                error={Boolean(fieldErrors.name)}
                                helperText={fieldErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Link"
                                name="link"
                                value={formData.link}
                                onChange={handleInputChange}
                                placeholder="https://facebook.com/yourpage"
                                error={Boolean(fieldErrors.link)}
                                helperText={fieldErrors.link}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Icon / Image</Typography>
                                <Box
                                    sx={{
                                        border: (fieldErrors.image || fieldErrors.general) ? '2px dashed #FF4D49' : '2px dashed #E0E0E0',
                                        borderRadius: '6px',
                                        p: 2,
                                        textAlign: 'center',
                                        bgcolor: '#FAFAFA',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        '&:hover': { borderColor: (fieldErrors.image || fieldErrors.general) ? '#FF4D49' : '#FFBD65' }
                                    }}
                                >
                                    {imagePreview ? (
                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                            <img src={imagePreview} alt="Preview"
                                                style={{ maxHeight: 120, maxWidth: '100%', borderRadius: '4px', display: 'block' }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={handleRemoveImage}
                                                sx={{
                                                    position: 'absolute',
                                                    top: -10,
                                                    right: -10,
                                                    bgcolor: '#FF4D49',
                                                    color: '#fff',
                                                }}
                                            >
                                                <X size={14} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <label style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer' }}>
                                            <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                                            <Stack spacing={1} alignItems="center">
                                                <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: '#FFF8ED', color: '#FFBD65', display: 'inline-flex' }}>
                                                    <Upload size={20} />
                                                </Box>
                                                <Typography sx={{ fontSize: '0.85rem', color: '#444' }}>Click to upload icon</Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>PNG or JPG (max. 2MB)</Typography>
                                            </Stack>
                                        </label>
                                    )}
                                </Box>
                                {(fieldErrors.image || fieldErrors.general) && (
                                    <Typography variant="caption" sx={{ color: '#FF4D49', mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                                        {fieldErrors.image || fieldErrors.general}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, borderTop: '1px solid #F0F0F0' }}>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleCloseModal}
                            sx={{ borderRadius: '6px', textTransform: 'none', color: '#222', fontSize: '0.85rem', px: 2.5 }}
                        >
                            Cancel
                        </Button>
                        <Button disableFocusRipple disableRipple disableTouchRipple
                            disableElevation onClick={handleSubmit} disabled={submitting} variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem',
                                '&:disabled': { bgcolor: '#F0F0F0' }
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update Link' : 'Add Link')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Social Link"
                    description="Are you sure you want to delete this social media link? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default SocialMedia;
